import { nextTick, onMounted, ref, watch } from 'vue'
import { useUserStore } from '@/stores/user'
import { useRecommendFeed } from './useRecommendFeed'
import { useCreationFeed } from './useCreationFeed'
import { useHomeGesture, type HomeTab } from './useHomeGesture'

export type FeedExpose = {
  jumpToIndex: (index: number) => void
  currentIndex: number
  resetNavigationState: () => void
}

export function useHomeFeed() {
  const userStore = useUserStore()

  const recommend = useRecommendFeed()
  const creation = useCreationFeed()

  const viewportRef = ref<HTMLElement | null>(null)
  const recommendPlayerRef = ref<FeedExpose | null>(null)
  const creationPlayerRef = ref<FeedExpose | null>(null)
  const creationCategoryScrollRef = ref<HTMLElement | null>(null)
  const creationCategoryButtonRefs = ref<Record<number, HTMLButtonElement | null>>({})

  const consumedAuthVersion = ref(userStore.authVersion)

  const tabOrder: HomeTab[] = ['recommend', 'creation']

  const gesture = useHomeGesture({
    tabOrder,
    viewportRef,
    onTabChange: (tab: HomeTab) => {
      if (tab === 'recommend' && !recommend.loaded.value) {
        void recommend.loadMore(recommendPlayerRef.value ?? undefined)
      }
      if (tab === 'creation' && !creation.loaded.value) {
        if (!creation.categoriesLoaded.value) {
          void creation.loadCategories()
        }
        void creation.loadMore(creationPlayerRef.value ?? undefined)
      }
    },
  })

  const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value))

  const scrollCreationCategoryToCenter = (categoryId: number, behavior: ScrollBehavior = 'smooth') => {
    const scrollContainer = creationCategoryScrollRef.value
    const targetButton = creationCategoryButtonRefs.value[categoryId]
    if (!scrollContainer || !targetButton) return

    const containerRect = scrollContainer.getBoundingClientRect()
    const buttonRect = targetButton.getBoundingClientRect()
    const buttonCenterOffset = buttonRect.left - containerRect.left + scrollContainer.scrollLeft + buttonRect.width / 2
    const rawLeft = buttonCenterOffset - scrollContainer.clientWidth / 2
    const maxLeft = Math.max(0, scrollContainer.scrollWidth - scrollContainer.clientWidth)

    scrollContainer.scrollTo({
      left: clamp(rawLeft, 0, maxLeft),
      behavior,
    })
  }

  const handleCreationCategoryChange = (categoryId: number) => {
    creation.changeCategory(categoryId, (id) => scrollCreationCategoryToCenter(id))
    creationPlayerRef.value?.jumpToIndex(0)
    void creation.loadMore(creationPlayerRef.value ?? undefined)
  }

  const reloadFeedsForAuthChange = async () => {
    recommend.reset()
    creation.reset()
    recommendPlayerRef.value?.jumpToIndex(0)
    creationPlayerRef.value?.jumpToIndex(0)

    if (!creation.categoriesLoaded.value) {
      await creation.loadCategories()
    }

    await Promise.all([
      recommend.loadMore(recommendPlayerRef.value ?? undefined),
      creation.loadMore(creationPlayerRef.value ?? undefined),
    ])
  }

  const stopCategoryTouch = (event: TouchEvent) => {
    event.stopPropagation()
  }

  // 初始化加载
  onMounted(async () => {
    gesture.updateViewportWidth()
    window.addEventListener('resize', gesture.updateViewportWidth)

    await Promise.all([
      recommend.loadMore(recommendPlayerRef.value ?? undefined),
      creation.loadCategories(),
    ])
    await creation.loadMore(creationPlayerRef.value ?? undefined)
  })

  // keepAlive 场景：登录/登出后刷新数据
  watch(
    () => userStore.authVersion,
    async () => {
      if (consumedAuthVersion.value === userStore.authVersion) return
      consumedAuthVersion.value = userStore.authVersion
      await reloadFeedsForAuthChange()
    },
  )

  return {
    // 状态
    activeTab: gesture.activeTab,
    activeTabIndex: gesture.activeTabIndex,

    // 推荐流
    recommend,

    // 创作流
    creation,

    // 手势
    gesture,

    // Refs
    viewportRef,
    recommendPlayerRef,
    creationPlayerRef,
    creationCategoryScrollRef,
    creationCategoryButtonRefs,

    // 方法
    handleCreationCategoryChange,
    scrollCreationCategoryToCenter,
    stopCategoryTouch,
  }
}
